import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { useHistory } from 'react-router';
import { Dispatch } from 'redux';
import { createPortal } from 'react-dom';

import { getUserAccountTypeIsInternal, getUserRoleSysName } from 'AuthPackage/MainModule/selectors/authSelectors';
import {
  ROUTE_AGREEMENTS,
  ROUTE_ANALYTICS,
  ROUTE_ANALYTICS_PROSECTOR,
  ROUTE_DECISIONS,
  ROUTE_FEEDBACK,
  ROUTE_KNM_LIST,
  ROUTE_NEWS,
  ROUTE_PREVENTION,
  ROUTE_REPORTS,
  ROUTE_SEARCH,
  ROUTE_SIMILARITIES,
  ROUTE_SYSTEM_INOPERABILITY,
  ROUTE_TEMPLATES,
  ROUTE_TEMPLATES_EXPORT,
  ROUTE_TEMPLATES_IMPORT,
} from 'CorePackage/MainModule/routes/routes';
import { IUserRole } from 'APIPackage/AuthModule/interfaces/IUserRole';
import { UserRoleEnum } from 'APIPackage/AuthModule/enums/UserRoleEnum';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';

import { MagicLine } from './MagicLine';
import styles from './NavBar.module.scss';
import { RootActions } from '../../MainModule/types/RootActions';
import { feedbackReset } from '../../../FeedbackPackage/MainModule/actions/feedbackActions';

interface IRoute {
  route: string;
  title: string;
  items?: IRoute[];
}

const links: IRoute[] = [
  {
    route: ROUTE_KNM_LIST,
    title: 'Список КНМ',
  },
  {
    route: ROUTE_PREVENTION,
    title: 'Список ПМ',
  },
  {
    route: ROUTE_DECISIONS,
    title: 'Иные решения',
  },
  {
    route: ROUTE_AGREEMENTS,
    title: 'Соглашения об устранении нарушений',
  },
  {
    route: ROUTE_TEMPLATES,
    title: 'Список планов',
  },
  {
    route: ROUTE_ANALYTICS,
    title: 'Аналитика',
    items: [
      {
        route: ROUTE_ANALYTICS,
        title: 'КНО',
      },
      {
        route: ROUTE_ANALYTICS_PROSECTOR,
        title: 'Орган прокуратуры',
      },
    ],
  },
  {
    route: ROUTE_TEMPLATES_IMPORT,
    title: 'Импорт/Экспорт',
    items: [
      {
        route: ROUTE_TEMPLATES_IMPORT,
        title: 'Импорт данных',
      },
      {
        route: ROUTE_TEMPLATES_EXPORT,
        title: 'Экспорт данных',
      },
    ],
  },
  {
    route: ROUTE_SIMILARITIES,
    title: 'Разрешение совпадений',
  },
  {
    route: ROUTE_SEARCH,
    title: 'Поиск проверок',
  },
  {
    route: ROUTE_NEWS,
    title: 'Новости',
    items: [
      {
        route: ROUTE_NEWS,
        title: 'Новостная лента',
      },
      {
        route: ROUTE_SYSTEM_INOPERABILITY,
        title: 'Сведения о неработоспособности',
      },
    ],
  },
  {
    route: ROUTE_REPORTS,
    title: 'Отчеты',
  },
  {
    route: ROUTE_FEEDBACK,
    title: 'Обратная связь',
  },
];

const ombLinks: IRoute[] = [
  {
    route: ROUTE_KNM_LIST,
    title: 'Список проверок',
  },
  {
    route: ROUTE_PREVENTION,
    title: 'Список ПМ',
  },
  {
    route: ROUTE_DECISIONS,
    title: 'Иные решения',
  },
  {
    route: ROUTE_AGREEMENTS,
    title: 'Соглашения об устранении нарушений',
  },
  {
    route: ROUTE_TEMPLATES_EXPORT,
    title: 'Экспорт данных',
  },
  {
    route: ROUTE_SEARCH,
    title: 'Поиск проверок',
  },
  {
    route: ROUTE_NEWS,
    title: 'Новости',
    items: [
      {
        route: ROUTE_NEWS,
        title: 'Новостная лента',
      },
      {
        route: ROUTE_SYSTEM_INOPERABILITY,
        title: 'Сведения о неработоспособности',
      },
    ],
  },
  {
    route: ROUTE_FEEDBACK,
    title: 'Обратная связь',
  },
];

const adminLinks: IRoute[] = [
  {
    route: ROUTE_SEARCH,
    title: 'Поиск проверок',
  },
  {
    route: ROUTE_NEWS,
    title: 'Новости',
    items: [
      {
        route: ROUTE_NEWS,
        title: 'Новостная лента',
      },
      {
        route: ROUTE_SYSTEM_INOPERABILITY,
        title: 'Сведения о неработоспособности',
      },
    ],
  },
  {
    route: ROUTE_FEEDBACK,
    title: 'Обратная связь',
  },
];

function mapRoutesToErknm(routes: IRoute[]): IRoute[] {
  return routes
    .filter(r => r.route !== ROUTE_SIMILARITIES)
    .map(r => {
      if (r.route === ROUTE_SEARCH) {
        return {
          ...r,
          title: 'Поиск мероприятий',
        };
      }

      return r;
    });
}

function mapRoutesToErp(routes: IRoute[]): IRoute[] {
  return routes
    .filter(r => r.route !== ROUTE_DECISIONS)
    .map(r => {
      if (r.route === ROUTE_ANALYTICS) {
        return {
          ...r,
          items: [],
        };
      }

      return r;
    });
}

interface INavBarStateProps extends RouteComponentProps {}

interface INavBarStoreProps {
  roleSysName: IUserRole['sysname'] | undefined;
  lawInterface: LawInterfacesEnum;
  isInternalAccount: boolean;
  settings: IAdminSetting[];
}

const NavBarLayout = ({
  location,
  roleSysName,
  lawInterface,
  isInternalAccount,
  settings,
}: INavBarStateProps & INavBarStoreProps) => {
  const activeRef = useRef<HTMLAnchorElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const [activeLeft, setActiveLeft] = useState(0);
  const [activeWidth, setActiveWidth] = useState(0);
  const [targetLeft, setTargetLeft] = useState(0);
  const [targetWidth, setTargetWidth] = useState(0);

  const [openRoute, setOpenRoute] = useState<string | null>(null);
  const [menuStyle, setMenuStyle] = useState<{ top: number; left: number; width: number } | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  const dispatch = useDispatch<Dispatch<RootActions>>();
  const history = useHistory();

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = undefined;
    }
  }, []);

  const openSubmenu = useCallback(
    (route: string) => {
      clearCloseTimer();
      const nav = navRef.current;

      if (!nav) {
        return;
      }
      const rect = nav.getBoundingClientRect();

      setMenuStyle({ top: rect.bottom, left: rect.left, width: rect.width });
      setOpenRoute(route);
    },
    [clearCloseTimer],
  );

  const scheduleCloseSubmenu = useCallback(() => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpenRoute(null), 120);
  }, [clearCloseTimer]);

  useEffect(() => {
    if (!openRoute) {
      return undefined;
    }
    const update = () => {
      const nav = navRef.current;

      if (!nav) {
        return;
      }
      const rect = nav.getBoundingClientRect();

      setMenuStyle({ top: rect.bottom, left: rect.left, width: rect.width });
    };

    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [openRoute]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  const isEnableAgreements = isLaterThanAdminSetting(
    new Date().toISOString(),
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_16_STARTS_FROM,
  );

  const isEnablePmForErp =
    settings?.find(setting => setting.code === AdminSettingCodesEnum.ENABLED_PM_FOR_ERP_4_1_19)?.value === 'true';

  const isAnalyticsEnable =
    settings?.find(setting => setting.code === AdminSettingCodesEnum.SHOW_ANALYTICS_CHAPTER)?.value === 'true';

  const isActive = (paths: Array<string>) => paths.some(path => location.pathname.includes(path));
  const onMouseEnter = useCallback((e: MouseEvent<HTMLElement>) => {
    setTargetLeft(e.currentTarget.offsetLeft);
    setTargetWidth(e.currentTarget.offsetWidth);
  }, []);

  function onMouseLeave() {
    setTargetLeft(activeLeft);
    setTargetWidth(activeWidth);
  }

  const linksToRender = useMemo(() => {
    switch (roleSysName) {
      case UserRoleEnum.OMBUDSMAN:
        return lawInterface === LawInterfacesEnum.FZ_248
          ? mapRoutesToErknm(ombLinks)
          : mapRoutesToErp(ombLinks.filter(r => isEnablePmForErp || r.route !== ROUTE_PREVENTION));
      case UserRoleEnum.SUPERVISOR:
        return lawInterface === LawInterfacesEnum.FZ_248
          ? mapRoutesToErknm(
              links
                .filter(r => {
                  return settings && settings.length && isEnableAgreements ? true : r.route !== ROUTE_AGREEMENTS;
                })
                .filter(r => {
                  return settings?.length && isAnalyticsEnable ? true : r.route !== ROUTE_ANALYTICS;
                })
                .map(r => {
                  if (r.route === ROUTE_TEMPLATES_IMPORT && !isInternalAccount) {
                    return {
                      ...r,
                      title: 'Экспорт',
                      route: ROUTE_TEMPLATES_EXPORT,
                      items: r.items?.filter(i => i.route !== ROUTE_TEMPLATES_IMPORT),
                    };
                  }

                  return r;
                })
                .map(r => {
                  if (r.route === ROUTE_ANALYTICS) {
                    return {
                      ...r,
                      title: 'Аналитика',
                      route: ROUTE_ANALYTICS,
                      items: undefined, // r.items?.filter(i => i.route !== ROUTE_ANALYTICS_PROSECTOR),
                    };
                  }

                  return r;
                }),
            )
          : mapRoutesToErp(
              links
                .filter(r => {
                  return settings?.length && isAnalyticsEnable ? true : r.route !== ROUTE_ANALYTICS;
                })
                .filter(r => r.route !== ROUTE_AGREEMENTS)
                .filter(r => isEnablePmForErp || r.route !== ROUTE_PREVENTION),
            );
      case UserRoleEnum.ADMIN:
        return lawInterface === LawInterfacesEnum.FZ_248 ? mapRoutesToErknm(adminLinks) : mapRoutesToErp(adminLinks);
      default:
        return lawInterface === LawInterfacesEnum.FZ_248
          ? mapRoutesToErknm(links)
          : mapRoutesToErp(links.filter(r => isEnablePmForErp || r.route !== ROUTE_PREVENTION));
    }
  }, [isInternalAccount, lawInterface, roleSysName, isEnableAgreements, settings]);

  useEffect(() => {
    if (activeRef.current !== null) {
      setActiveLeft(activeRef.current.offsetLeft);
      setActiveWidth(activeRef.current.offsetWidth);
      setTargetLeft(activeRef.current.offsetLeft);
      setTargetWidth(activeRef.current.offsetWidth);
    }
  }, [lawInterface]);

  const feedbackRoute = useCallback(() => {
    dispatch(feedbackReset());
    history.push(ROUTE_FEEDBACK);
  }, [dispatch, history]);

  const openLink = openRoute ? linksToRender.find(l => l.route === openRoute) : undefined;

  return (
    <nav ref={navRef} className={styles.NavWrapper}>
      {linksToRender.map((link, i) => {
        const subRoutes = link.items ? link.items.map(sublink => sublink.route) : [];
        const linkRoutes = [link.route, ...subRoutes];
        const isLinkActive = isActive(linkRoutes);
        const linkAttributes = {
          ...(isLinkActive && { 'data-active': true }),
        };

        return (
          <div
            className={styles.NavItem}
            key={i}
            id={link.route}
            onMouseEnter={e => {
              onMouseEnter(e);
              if (link.items?.length) {
                openSubmenu(link.route);
              }
            }}
            onMouseLeave={() => {
              onMouseLeave();
              scheduleCloseSubmenu();
            }}
          >
            <Link
              ref={isLinkActive ? activeRef : null}
              className={styles.NavLink}
              {...linkAttributes}
              to={link.route}
              onClick={link.route === ROUTE_FEEDBACK ? feedbackRoute : undefined}
            >
              {link.title}
            </Link>

            {link.items && (
              <div className={styles.NavSubitems}>
                {link.items.map(sublink => {
                  const subLinkAttributes = {
                    ...(isActive([sublink.route]) && { 'data-active': true }),
                  };

                  return (
                    <div className={styles.NavSubitem} key={sublink.route}>
                      <Link className={styles.NavSublink} {...subLinkAttributes} to={sublink.route}>
                        {sublink.title}
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {activeWidth !== 0 && <MagicLine targetLeft={targetLeft} targetWidth={targetWidth} />}

      {openLink?.items &&
        menuStyle &&
        createPortal(
          <div
            className={styles.NavSubitems}
            style={{
              position: 'fixed',
              display: 'flex',
              boxSizing: 'border-box',
              top: menuStyle.top,
              left: menuStyle.left,
              width: menuStyle.width,
            }}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={scheduleCloseSubmenu}
          >
            {openLink.items.map(sublink => {
              const subLinkAttributes = {
                ...(isActive([sublink.route]) && { 'data-active': true }),
              };

              return (
                <div className={styles.NavSubitem} key={sublink.route}>
                  <Link
                    className={styles.NavSublink}
                    {...subLinkAttributes}
                    to={sublink.route}
                    onClick={() => setOpenRoute(null)}
                  >
                    {sublink.title}
                  </Link>
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </nav>
  );
};

const mapStateToProps = (state: IRootState): INavBarStoreProps => ({
  roleSysName: getUserRoleSysName(state),
  lawInterface: getLawInterface(state),
  settings: getAdminFetchSettings(state),
  isInternalAccount: getUserAccountTypeIsInternal(state),
});

export const NavBar = connect(mapStateToProps)(withRouter(NavBarLayout));
